import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  useTheme
} from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';
import { useGlobalConfig } from 'context/GlobalContext';
import { ANALYTICS_EVENTS } from 'framework/analytics/AnalyticsEvents';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectLastFetchedPilotId,
  setLevel1Data,
  setLevel2Data,
  setLoading,
  setError,
  setLastFetchedPilotId
} from 'store/dataReducers/featureMetricsSlice';
import { createCxApiClient } from 'framework/http/CxEditorClient';
import { DataScenariosSidebar } from './DataScenariosSidebar';
import { TemplateLevelScenarios } from './TemplateLevelScenarios';
import { 
  DataScenariosApi, 
  DataScenario, 
  transformToDataScenario,
  getTemplateNameForApi,
  getFuelTypeFromTemplateKey
} from 'apis/dataScenariosAPI';
import { useProjectFuelType } from 'hooks/useProjectFuelTypes';

// Global pilot ID constant - used for all Global Data Scenarios API calls
const GLOBAL_PILOT_ID = '601';

export const GlobalDataScenariosPage: React.FC = () => {
  const { rc } = useGlobalConfig();
  const theme = useTheme();
  const dispatch = useDispatch();
  const lastFetchedPilotId = useSelector(selectLastFetchedPilotId);
  const { projectFuelTypes } = useProjectFuelType(rc, GLOBAL_PILOT_ID, undefined);
  const [selectedTemplateKey, setSelectedTemplateKey] = useState<string | undefined>(undefined);
  const [selectedSectionKey, setSelectedSectionKey] = useState<string | undefined>(undefined);
  const [selectedSectionId, setSelectedSectionId] = useState<string | undefined>(undefined);
  const [selectedSectionName, setSelectedSectionName] = useState<string | undefined>(undefined);
  
  // State for template scenarios
  const [templateScenarios, setTemplateScenarios] = useState<DataScenario[]>([]);
  const [scenariosLoading, setScenariosLoading] = useState(false);
  const [scenariosError, setScenariosError] = useState<string | null>(null);
  
  const [loadedTemplateKey, setLoadedTemplateKey] = useState<string | undefined>(undefined);

  // Fire scenarios-list-opened analytics once on mount
  useEffect(() => {
    rc.analytics.trackEvent(ANALYTICS_EVENTS.CONTENT.SCENARIOS_LIST_OPENED)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch L1/L2 data on mount (using GLOBAL_PILOT_ID)
  useEffect(() => {
    if (lastFetchedPilotId === GLOBAL_PILOT_ID) return;

    let ignore = false;

    async function fetchFeatureMetrics() {
      dispatch(setLoading(true));
      dispatch(setError(null));

      try {
        const api = createCxApiClient(rc, process.env.REACT_APP_TOOL_API_URL as string);

        console.log('[GlobalDataScenariosPage] Fetching Level 1 and Level 2 feature metrics for global pilot:', GLOBAL_PILOT_ID);

        // Fetch both APIs in parallel
        const [level1Response, level2Response] = await Promise.all([
          api.getFeatureMetrics(GLOBAL_PILOT_ID, 'LEVEL_1'),
          api.getFeatureMetrics(GLOBAL_PILOT_ID, 'LEVEL_2')
        ]);

        if (!ignore) {
          dispatch(setLevel1Data(level1Response));
          dispatch(setLevel2Data(level2Response));
          dispatch(setLastFetchedPilotId(GLOBAL_PILOT_ID));
          dispatch(setLoading(false));
          console.log('[GlobalDataScenariosPage] Feature metrics loaded successfully', {
            level1Count: level1Response.length,
            level2Count: level2Response.length
          });
        }
      } catch (err: any) {
        if (!ignore) {
          console.error('[GlobalDataScenariosPage] Failed to fetch feature metrics:', err);
          dispatch(setError(err.message || 'Failed to fetch feature metrics'));
        }
      }
    }

    fetchFeatureMetrics();
    return () => { ignore = true; };
  }, [lastFetchedPilotId, dispatch, rc]);

  // Helper function to fetch scenarios for a template
  const fetchScenariosForTemplate = async (templateKey: string, forceRefresh?: boolean): Promise<DataScenario[]> => {
    const templateNameForApi = getTemplateNameForApi(templateKey);
    const fuelType = getFuelTypeFromTemplateKey(templateKey);
    
    const response = await DataScenariosApi.getDataScenarios(rc, {
      templateName: templateNameForApi,
      pilotId: GLOBAL_PILOT_ID,
      forceRefresh: forceRefresh,
      fuelType: fuelType,
    });

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch scenarios');
    }

    return response.data.map(transformToDataScenario);
  };

  useEffect(() => {
    if (!selectedTemplateKey) {
      setTemplateScenarios([]);
      setLoadedTemplateKey(undefined);
      return;
    }

    if (loadedTemplateKey === selectedTemplateKey) {
      return;
    }

    let ignore = false;

    async function fetchTemplateScenarios() {
      setScenariosLoading(true);
      setScenariosError(null);

      try {
        const scenarios = await fetchScenariosForTemplate(selectedTemplateKey as string);

        if (!ignore) {
          setTemplateScenarios(scenarios);
          setLoadedTemplateKey(selectedTemplateKey);
          setScenariosLoading(false);
          console.log('[GlobalDataScenariosPage] Scenarios loaded successfully:', scenarios.length);
        }
      } catch (err: any) {
        if (!ignore) {
          console.error('[GlobalDataScenariosPage] Failed to fetch scenarios:', err);
          setScenariosError(err.message || 'Failed to fetch scenarios');
          setScenariosLoading(false);
        }
      }
    }

    fetchTemplateScenarios();
    return () => { ignore = true; };
  }, [selectedTemplateKey, loadedTemplateKey]);

  const selectedTemplate = selectedTemplateKey
    ? { templateKey: selectedTemplateKey, name: selectedTemplateKey, channel: 'Email' as const }
    : null;

  const handleSelectTemplate = (templateKey: string) => {
    setSelectedTemplateKey(templateKey);
    setSelectedSectionKey(undefined);
    setSelectedSectionId(undefined);
    setSelectedSectionName(undefined);
  };

  const handleSelectSection = async (templateKey: string, sectionKey: string, sectionId: string, sectionName: string) => {
    setSelectedSectionKey(sectionKey);
    setSelectedSectionId(sectionId);
    setSelectedSectionName(sectionName);

    if (templateKey !== loadedTemplateKey) {
      setSelectedTemplateKey(templateKey);
      setScenariosLoading(true);
      setScenariosError(null);

      try {
        const scenarios = await fetchScenariosForTemplate(templateKey);
        setTemplateScenarios(scenarios);
        setLoadedTemplateKey(templateKey);
        setScenariosLoading(false);
        // console.log('[GlobalDataScenariosPage] Scenarios loaded for section click:', scenarios.length);
      } catch (err: any) {
        console.error('[GlobalDataScenariosPage] Failed to fetch scenarios:', err);
        setScenariosError(err.message || 'Failed to fetch scenarios');
        setScenariosLoading(false);
      }
    } else {
      setSelectedTemplateKey(templateKey);
    }
  };

  // Filter scenarios by fuel type (same as DataScenariosPage) then by selected section
  const displayedScenarios = React.useMemo(() => {
    const projectFuelTypesUpper = (projectFuelTypes ?? []).map(f =>
      (f || '').trim().toUpperCase()
    );
    const fuelFilteredScenarios = templateScenarios.filter(scenario => {
      const scenarioFuelType = (scenario.fuelType || '').trim().toUpperCase();
      if (!scenarioFuelType) return true;
      if (projectFuelTypesUpper.length === 0) return true;
      return projectFuelTypesUpper.includes(scenarioFuelType);
    });

    if (!selectedSectionId) {
      return fuelFilteredScenarios.filter(scenario =>
        scenario.elementNames.includes('FULL_TEMPLATE') ||
        (selectedTemplateKey && scenario.elementNames.includes(selectedTemplateKey))
      );    
    }
    return fuelFilteredScenarios.filter(scenario => {
      const specificElements = scenario.elementNames.filter(name => name !== 'FULL_TEMPLATE');
      const hasFullTemplate = scenario.elementNames.includes('FULL_TEMPLATE');
      
      if (selectedSectionId === 'FULL_TEMPLATE') {
        return hasFullTemplate;
      }
      
      if (specificElements.length === 0) {
        return false;
      }
      
      if (specificElements.length === 1) {
        return specificElements[0] === selectedSectionId;
      }
      

      return specificElements.includes(selectedSectionId);
    });
  }, [templateScenarios, selectedSectionId, selectedTemplateKey, projectFuelTypes]);

  return (
    <Box sx={{ 
      bgcolor: theme.palette.background.default, 
      minHeight: '100vh', 
      py: 4,
      position: 'relative'
    }}>
      <Box sx={{ maxWidth: 1600, mx: 'auto', px: 4 }}>
        {/* Header Banner */}
        <Card sx={{ 
          p: 0, 
          borderRadius: 3, 
          boxShadow: 3,
          bgcolor: theme.palette.background.paper,
          mb: 3
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            px: 4, 
            pt: 4 
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                <StorageIcon sx={{ fontSize: 30, marginRight: 1, color: theme.palette.primary.main }} />
                <Typography 
                  variant="h4" 
                  fontWeight={600} 
                  sx={{ 
                    mb: 0.5,
                    textAlign: 'left',
                    color: theme.palette.text.primary
                  }}
                >
                  Global Data Scenarios
                </Typography>
              </Box>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  color: theme.palette.text.secondary,
                  mt: 1,
                  mb: 1.2
                }}
              >
                Define and maintain the master list of GLOBAL data scenarios for all products and features.
              </Typography>
            </Box>
          </Box>
        </Card>

        {/* Main Content Area */}
        <Box sx={{ display: 'flex', gap: 3, height: 'calc(100vh - 280px)' }}>
          {/* Left Sidebar - Templates */}
          <Box sx={{ width: 320, flexShrink: 0 }}>
            <DataScenariosSidebar
              selectedTemplateKey={selectedTemplateKey}
              selectedSectionKey={selectedSectionKey}
              onSelectTemplate={handleSelectTemplate}
              onSelectSection={handleSelectSection}
              isOpen={true}
              showAllFuelTypes={true}
              pilotId={GLOBAL_PILOT_ID}
            />
          </Box>

          {/* Main Content Area - Template Scenarios */}
          <Card sx={{ 
            flex: 1,
            borderRadius: 2,
            boxShadow: 2,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', p: 3 }}>
              <TemplateLevelScenarios
                selectedTemplate={selectedTemplate || null}
                selectedSection={selectedSectionId ? { id: selectedSectionId, name: selectedSectionName || '' } : undefined}
                scenarios={displayedScenarios}
                loading={scenariosLoading}
                error={scenariosError}
                pilotId={GLOBAL_PILOT_ID}
                isGlobal={true}
                onAddProjectScenario={async () => {
                  if (selectedTemplateKey) {
                    console.log('[GlobalDataScenariosPage] Refreshing scenarios after add');
                    setScenariosLoading(true);
                    try {
                      const scenarios = await fetchScenariosForTemplate(selectedTemplateKey, true);
                      setTemplateScenarios(scenarios);
                      setScenariosLoading(false);
                    } catch (err: any) {
                      console.error('[GlobalDataScenariosPage] Failed to refresh scenarios:', err);
                      setScenariosError(err.message || 'Failed to refresh scenarios');
                      setScenariosLoading(false);
                    }
                  }
                }}
                onCopyScenario={(scenarioId) => {
                  console.log('Copy scenario:', scenarioId);
                }}
                onEditScenario={async (scenarioId) => {
                  console.log('[GlobalDataScenariosPage] Refreshing scenarios after edit:', scenarioId);
                  if (selectedTemplateKey) {
                    setScenariosLoading(true);
                    try {
                      const scenarios = await fetchScenariosForTemplate(selectedTemplateKey, true);
                      setTemplateScenarios(scenarios);
                      setScenariosLoading(false);
                    } catch (err: any) {
                      console.error('[GlobalDataScenariosPage] Failed to refresh scenarios:', err);
                      setScenariosError(err.message || 'Failed to refresh scenarios');
                      setScenariosLoading(false);
                    }
                  }
                }}
                onDeleteScenario={async (scenarioId) => {
                  console.log('[GlobalDataScenariosPage] Refreshing scenarios after delete:', scenarioId);
                  if (selectedTemplateKey) {
                    setScenariosLoading(true);
                    try {
                      const scenarios = await fetchScenariosForTemplate(selectedTemplateKey, true);
                      setTemplateScenarios(scenarios);
                      setScenariosLoading(false);
                    } catch (err: any) {
                      console.error('[GlobalDataScenariosPage] Failed to refresh scenarios:', err);
                      setScenariosError(err.message || 'Failed to refresh scenarios');
                      setScenariosLoading(false);
                    }
                  }
                }}
              />
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

